// Admin page listing investors and creating new ones (investor + first SUPERADMIN).
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorMessage, captureException } from '@startus/lib';
import { useInvestors } from '../hooks/useInvestors';
import { investorsService } from '../services/investors.service';
import CreateInvestorModal, {
  type CreateInvestorSubmitErrors,
} from '../components/CreateInvestorModal';
import { fieldErrorsFromApiError } from '../utils/investorForm';
import type { CreateInvestorPayload, InvestorCreated } from '../types/investor';

export default function InvestorsPage() {
  const { t } = useTranslation('admin');
  const { investors, loading, error, reload } = useInvestors();
  const [modalOpen, setModalOpen] = useState(false);
  // Incremented on each open so the modal remounts with a clean form.
  const [modalKey, setModalKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<InvestorCreated | null>(null);

  const existingSlugs = useMemo(() => new Set(investors.map((i) => i.slug)), [investors]);

  const openModal = () => {
    setCreated(null);
    setModalKey((k) => k + 1);
    setModalOpen(true);
  };

  // Errors are returned to the modal (a toast would render beneath it): known
  // conflicts go inline on their field, anything else as a generic modal alert.
  const handleCreate = async (
    payload: CreateInvestorPayload
  ): Promise<CreateInvestorSubmitErrors | void> => {
    setSubmitting(true);
    try {
      const result = await investorsService.createInvestor(payload);
      setCreated(result);
      reload();
    } catch (err: unknown) {
      const fields = fieldErrorsFromApiError(err);
      if (fields) return { fields }; // expected 409 — user fixes the field, not a bug
      captureException(err, { slug: payload.slug });
      return { form: getErrorMessage(err, t) };
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 flex flex-col gap-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('investors.title')}</h1>
          <p className="text-sm text-base-content/60 mt-1">{t('investors.subtitle')}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openModal}>
          {t('investors.add')}
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-base-300">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : error ? (
          <div className="text-center py-16 text-error" role="alert">
            {getErrorMessage(error, t)}
          </div>
        ) : investors.length === 0 ? (
          <div className="text-center py-16 text-base-content/50">{t('investors.empty')}</div>
        ) : (
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>{t('investors.table.name')}</th>
                <th>{t('investors.table.slug')}</th>
              </tr>
            </thead>
            <tbody>
              {investors.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.name}</td>
                  <td>
                    <code className="font-mono text-sm bg-base-200 px-2 py-0.5 rounded">
                      {inv.slug}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <CreateInvestorModal
          key={modalKey}
          existingSlugs={existingSlugs}
          created={created}
          submitting={submitting}
          onSubmit={handleCreate}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
